/**
 * Zod validation schemas for user roles
 *
 * These schemas provide runtime validation for user role data,
 * complementing the TypeScript compile-time types from Drizzle.
 */
import { z } from 'zod';
import { HR_ROLE_RANK } from '../config/hrRoles.js';
/**
 * Valid role values for RBAC system.
 *
 * Afgeleid van de rangorde in `config/hrRoles.ts` — één lijst, één waarheid.
 */
export const RoleEnum = z.enum(HR_ROLE_RANK);
/**
 * Schema for inserting a new user role
 */
export const InsertUserRoleSchema = z.object({
    userId: z.number().int().positive({
        message: 'User ID must be a positive integer'
    }),
    role: RoleEnum,
    managerId: z.number().int().positive({
        message: 'Manager ID must be a positive integer'
    }).nullable().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional()
});
/**
 * Schema for a complete user role (from database)
 */
export const UserRoleSchema = z.object({
    userId: z.number().int().positive(),
    role: RoleEnum,
    managerId: z.number().int().positive().nullable(),
    createdAt: z.string(),
    updatedAt: z.string()
});
/**
 * Schema for updating a user role
 */
export const UpdateUserRoleSchema = z.object({
    role: RoleEnum.optional(),
    managerId: z.number().int().positive().nullable().optional()
}).refine((data) => data.role !== undefined || data.managerId !== undefined, {
    message: 'At least one field (role or managerId) must be provided for update'
});
/**
 * Schema for role assignment with validation rules
 */
export const RoleAssignmentSchema = z.object({
    userId: z.number().int().positive(),
    role: RoleEnum,
    managerId: z.number().int().positive().nullable()
}).refine((data) => {
    // Medewerker must have a manager
    if (data.role === 'medewerker') {
        return data.managerId !== null;
    }
    return true;
}, {
    message: 'Medewerker role requires a manager to be assigned',
    path: ['managerId']
}).refine((data) => {
    // Admin, HR, and Beoordelaar should not have a manager
    if (['admin', 'hr', 'beoordelaar'].includes(data.role)) {
        return data.managerId === null;
    }
    return true;
}, {
    message: 'Only medewerker role can have a manager',
    path: ['managerId']
});
//# sourceMappingURL=userRole.js.map